import { get, omitBy } from "lodash/fp";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Checkbox,
  Container,
  Content,
  FlexboxGrid,
  Footer,
  Header,
  Pagination,
  Table,
  Input,
  InputGroup,
  Loader,
  SelectPicker,
} from "rsuite";
import SearchIcon from "@rsuite/icons/Search";
import CloseIcon from "@rsuite/icons/Close";
import { CommercialScheduleServices } from "../../../core/services/CommercialScheduleServices";
import { UtilityServices } from "../../../core/services/UtilityServices";
import {
  CommercialFields,
  CommercialListParams,
} from "../Commercials/CommercialDataTypes";
import { Permission } from "../../../core/types/PermissionInterface";
import "./CommercialSchedules.css";
import {
  isEmptyVal,
  isNotEmpty,
  displayField,
  countCommaSeparatedVals,
} from "../../../utils/helpers";
import CustomCell from "../../components/Elements/CustomCell";
import CustomHeaderCell from "../../components/Elements/CustomHeaderCell";
import CheckCell from "../../components/Elements/CheckCell";
import { ToastContainer } from "react-toastify";
import {
  messageTypeArr,
  appConstObj,
  roleTypeArr,
} from "../../../core/config/AppConstants";
import { useTranslation } from "react-i18next";
import CommercialScheduleStoreBulkAction from "./CommercialScheduleStoreBulkAction";
import { updateIn } from "json-immut";
import { CommercialScheduleFields } from "./CommercialScheduleDataTypes";
import { StoreServices } from "../../../core/services/StoreServices";
import { useSelector } from "react-redux";
import PhoneCell from "../../components/Elements/PhoneCell";
import AddressCell from "../../components/Elements/AddressCell";

function CommercialScheduleStores({
  commercialScheduleId,
  excludeCommercialSchedule,
  companyId,
  withHeader = true,
  onDeleteStores,
  enableBulkAction = [],
  storeSearchParams = {
    smart_search: "",
    region: "",
    group: "",
  },
  setSelectedStores,
  ...props
}: {
  commercialScheduleId?: number;
  excludeCommercialSchedule?: number;
  companyId?: number;
  withHeader?: boolean;
  onDeleteStores?: Function;
  enableBulkAction?: string[];
  storeSearchParams?: any;
  setSelectedStores?: Function;
}) {
  const { t } = useTranslation();
  const { Cell, Column } = Table;
  const company = useSelector((state: any) => state.company);
  const [regionList, setRegionList] = useState([]);
  const [storeGroupList, setStoreGroupList] = useState([]);
  const [commercialScheduleInfo, setCommercialScheduleInfo] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [loadingSchedule, setLoadingSchedule] = useState(false);
  const [rows, setRows] = useState(appConstObj.record_per_page);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [data, setData] = useState<CommercialFields[]>([]);
  const [sort_by, setSortBy] = useState<any>();
  const [sort_order, setSortOrder] = useState<any>();
  const [smartSearchInput, setSmartSearch] = useState<string>(
    storeSearchParams.smart_search
  );
  const [storeRegionSearch, setStoreRegionSearch] = useState<string>(
    storeSearchParams.region
  );
  const [storeGroupSearch, setStoreGroupSearch] = useState<string>(
    storeSearchParams.group
  );
  const loggedInUserDetail = UtilityServices.getLoggedInUserDetails();
  const initSearchFormData = {
    smart_search: storeSearchParams.smart_search,
    region: storeSearchParams.region,
    group: storeSearchParams.group,
  };

  const [formData, setFormData] =
    useState<CommercialListParams>(initSearchFormData);
  const navigate = useNavigate();
  const [checkedKeys, setCheckedKeys] = useState<any[]>([]);
  let checked = false;
  let indeterminate = false;

  if (checkedKeys?.length === data?.length) {
    checked = true;
  } else if (checkedKeys?.length === 0) {
    checked = false;
  } else if (checkedKeys?.length > 0 && checkedKeys?.length < data?.length) {
    indeterminate = true;
  }

  const handleCheckAll = (value: any, checked: any) => {
    const keys = checked ? data.map((store) => store.id) : [];
    setCheckedKeys(keys);
    setSelectedStores && setSelectedStores(keys);
  };

  const handleCheck = (value: number, checked: any) => {
    const keys = checked
      ? [...checkedKeys, value]
      : checkedKeys.filter((store) => store !== value);
    setCheckedKeys(keys);
    setSelectedStores && setSelectedStores(keys);
  };

  async function getRegionList(companyId?: number) {
    let compId: number = companyId ? companyId : getCompanyId();
    setRegionList([]);
    if (!compId) {
      return;
    }
    let params: any = {};
    if (compId) {
      params.company = compId;
    }

    try {
      const res = await StoreServices.getRegions(params);
      const respData = get("data.data", res);
      if (respData.length) {
        setRegionList(respData);
      }
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  async function handleRegionChange(regionId: number) {
    try {
      const res = await StoreServices.getStoreGroups({
        region: regionId,
      });
      const respData = get("data.data", res);
      setStoreGroupList(respData);
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  function getCompanyId() {
    let compId = null;
    if (UtilityServices.getLoggedInUser("user_type") === roleTypeArr.client) {
      compId = UtilityServices.getLoggedInUser("company_id");
    } else if (company?.id) {
      compId = company.id;
    } else if (companyId > 0) {
      compId = companyId;
    }
    return compId;
  }

  async function getCommercialScheduleStores(params: any) {
    try {
      let cmpId = getCompanyId();
      if (isEmptyVal(commercialScheduleId) && isEmptyVal(cmpId)) return;
      if (!isEmptyVal(cmpId)) params.company = cmpId;
      if (!isEmptyVal(commercialScheduleId))
        params.commercial_schedule = commercialScheduleId;
      if (!isEmptyVal(excludeCommercialSchedule))
        params.exclude_commercial_schedule = excludeCommercialSchedule;
      setLoading(true);
      const res = await StoreServices.getStores(params);
      setData(get("data.data", res));
      setTotal(get("data.total", res));
      setCheckedKeys([]);
      setSelectedStores && setSelectedStores([]);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  }

  async function getViewCommercialScheduleDetail(id: number) {
    try {
      setLoadingSchedule(true);
      const res = await CommercialScheduleServices.viewCommercialSchedule(id);

      setCommercialScheduleInfo({ ...res?.data?.data });
      setRows(countCommaSeparatedVals(res?.data?.data?.store_ids));
      getCommercialScheduleStores(
        getSearchParams({
          page: page,
          sort_by: sort_by,
          sort_order: sort_order,
          rows: countCommaSeparatedVals(res?.data?.data?.store_ids),
        })
      );
      getRegionList(res?.data?.data?.company_id);
      setLoadingSchedule(false);
    } catch (error) {
      setLoadingSchedule(false);
    }
  }

  const handleSearchFormChange = (data: any) => {
    let form = updateIn(formData, [data.path], data.value);
    setFormData(form);
  };

  async function init() {
    if (commercialScheduleId > 0) {
      await getViewCommercialScheduleDetail(commercialScheduleId);
    } else if (getCompanyId() > 0) {
      getRegionList();
      getCommercialScheduleStores(
        getSearchParams({
          page: page,
          sort_by: sort_by,
          sort_order: sort_order,
        })
      );
    }
  }

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    getRegionList();
    getCommercialScheduleStores(
      getSearchParams({
        page: page,
        sort_by: sort_by,
        sort_order: sort_order,
      })
    );
  }, [company.id]);

  async function bulkDeleteAction() {
    try {
      let params: CommercialScheduleFields = {
        remove_store_ids: checkedKeys.join(),
      };
      let res = await CommercialScheduleServices.updateCommercialSchedule(
        commercialScheduleId,
        params
      );
      UtilityServices.showToaster({
        message: res.data.message,
      });

      onDeleteStores && onDeleteStores(commercialScheduleId);
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  function getSearchParams(data?: any) {
    let paramsData = {
      ...formData,
      ...{ smart_search: smartSearchInput },
      ...{ group: storeGroupSearch },
      ...{ region: storeRegionSearch },
      ...{ sort_by, sort_order },
      ...{ rows },
      ...data,
    };
    let params: CommercialListParams = omitBy(isEmptyVal, paramsData);
    if (data?.page) {
      params.page = data?.page;
    }
    let cmpId = getCompanyId();
    if (cmpId > 0) {
      params.company = cmpId;
    }
    return params;
  }

  function onSortChange(dataKey: any, sortType: any) {
    setPage(1);
    setSortBy(dataKey);
    setSortOrder(sortType);
    getCommercialScheduleStores(
      getSearchParams({
        page: 1,
        sort_by: dataKey,
        sort_order: sortType,
        rows: rows,
      })
    );
  }

  const filterCommercialScheduleStores = (e: any) => {
    if (e.key === "Enter" || e.type === "click") {
      getCommercialScheduleStores(
        getSearchParams({ smart_search: smartSearchInput, rows: rows })
      );
    }
  };

  const clearSmartSearch = () => {
    setSmartSearch("");
    setStoreGroupSearch("");
    setStoreRegionSearch("");
    setSortBy(null);
    setSortOrder(null);
    setPage(1);
    getCommercialScheduleStores(
      getSearchParams({
        smart_search: "",
        group: "",
        region: "",
        page: 1,
        sort_by: null,
        sort_order: null,
      })
    );
  };

  const deletePermission = UtilityServices.hasPermission(
    "update-commercial-schedule"
  );
  const permissionObj: Permission = {
    delete: deletePermission,
  };

  return (
    <div className="commercial-schedule-items">
      {loadingSchedule && (
        <Loader
          style={{ zIndex: 9999 }}
          size="lg"
          content="Loading..."
          center
          backdrop={true}
        />
      )}
      <Container className="container">
        {withHeader && (
          <Header className="h4">
            <FlexboxGrid justify="space-between">
              <FlexboxGrid.Item colspan={24}>
                <h4>
                  {t("COMMERCIAL_SCHEDULE")}{" "}
                  {commercialScheduleInfo?.schedule_title} {t("STORES")}
                </h4>
              </FlexboxGrid.Item>
            </FlexboxGrid>
          </Header>
        )}

        <Content className="commercial-schedule-items">
          {UtilityServices.hasLoggedInUserType(roleTypeArr.backend) && (
            <>
              <div className="row">
                <CommercialScheduleStoreBulkAction
                  commercialScheduleData={commercialScheduleInfo}
                  checkedKeys={checkedKeys}
                  setCheckedKeys={setCheckedKeys}
                  onDeleteStores={bulkDeleteAction}
                  {...(enableBulkAction.length > 0 && { enableBulkAction })}
                />
                <div className="col-md-8">
                  <InputGroup>
                    <SelectPicker
                      data={regionList}
                      valueKey="id"
                      labelKey="region_name"
                      onSelect={(value, item, e) => {
                        handleSearchFormChange({
                          path: "region",
                          value,
                        });
                        setStoreRegionSearch(value);
                        handleRegionChange(value);
                        getCommercialScheduleStores(
                          getSearchParams({
                            region: value,
                            group: "",
                          })
                        );
                      }}
                      onClean={(e: any) => {
                        handleSearchFormChange({
                          path: "region",
                          value: "",
                        });
                        handleSearchFormChange({
                          path: "group",
                          value: "",
                        });
                        setStoreRegionSearch("");
                        setStoreGroupSearch("");
                        setStoreGroupList([]);
                        getCommercialScheduleStores(
                          getSearchParams({
                            region: "",
                            group: "",
                          })
                        );
                      }}
                      name="region"
                      value={storeRegionSearch}
                      placeholder={t("REGION")}
                    />

                    <SelectPicker
                      data={storeGroupList}
                      valueKey="id"
                      labelKey="group_name"
                      onSelect={(value, item, e) => {
                        handleSearchFormChange({ path: "group", value });
                        setStoreGroupSearch(value);
                        getCommercialScheduleStores(
                          getSearchParams({ group: value })
                        );
                      }}
                      onClean={(e: any) => {
                        handleSearchFormChange({
                          path: "group",
                          value: "",
                        });
                        setStoreGroupSearch("");
                        getCommercialScheduleStores(
                          getSearchParams({ group: "" })
                        );
                      }}
                      name="group"
                      value={storeGroupSearch}
                      placeholder={t("STORE") + " " + t("GROUP")}
                    />
                    <Input
                      className="form-control"
                      placeholder={t("SMART_SEARCH_BY", {
                        entity:
                          t("STORE").toLowerCase() +
                          " " +
                          t("NAME").toLowerCase() +
                          t("OR").toLowerCase() +
                          " " +
                          t("CODE").toLowerCase(),
                      })}
                      value={smartSearchInput}
                      onChange={(val: any) => setSmartSearch(val)}
                      onKeyDown={(e: any) => filterCommercialScheduleStores(e)}
                    />
                    <InputGroup.Button
                      title={t("SEARCH")}
                      onClick={(e: any) => filterCommercialScheduleStores(e)}
                    >
                      <SearchIcon />
                    </InputGroup.Button>
                    <InputGroup.Button
                      title={t("CLEAR")}
                      onClick={() => clearSmartSearch()}
                    >
                      <CloseIcon />
                    </InputGroup.Button>
                  </InputGroup>
                </div>
              </div>
            </>
          )}

          <div className={total > 0 ? "commercial-table" : "no-record-table"}>
            <Table
              height={400}
              fillHeight={true}
              data={data}
              loading={loading}
              onSortColumn={onSortChange}
              sortColumn={sort_by}
              bordered={true}
              cellBordered
              sortType={sort_order}
              hover={true}
              renderEmpty={() => (
                <div style={{ textAlign: "center", padding: "20px" }}>
                  <h4>
                    {t("NO_ENTITY_FOUND_FOR", {
                      entity: t("STORE"),
                      for: commercialScheduleInfo?.schedule_title,
                    })}
                  </h4>
                </div>
              )}
            >
              <Column width={40} align="center" fixed>
                <CustomHeaderCell>
                  <div>
                    <Checkbox
                      inline
                      checked={checked}
                      indeterminate={indeterminate}
                      onChange={handleCheckAll}
                    />
                  </div>
                </CustomHeaderCell>
                <CheckCell
                  dataKey="id"
                  checkedKeys={checkedKeys}
                  onChange={handleCheck}
                />
              </Column>

              <Column width={200} sortable resizable fixed>
                <CustomHeaderCell>
                  {t("STORE")} {t("NAME")}
                </CustomHeaderCell>
                <CustomCell dataKey="store_name">
                  {(rowData: any) => (
                    <span
                      title={
                        (isNotEmpty(`${rowData.store_code}`) &&
                          `${rowData.store_code}`) ||
                        `${rowData.store_name}`
                      }
                    >
                      {displayField(rowData.store_name)}
                    </span>
                  )}
                </CustomCell>
              </Column>
              <Column flexGrow={1} resizable>
                <CustomHeaderCell>{t("ADDRESS")}</CustomHeaderCell>
                <AddressCell rowData={(rowData: any) => rowData} />
              </Column>
              <Column width={120}>
                <CustomHeaderCell>{t("PHONE")}</CustomHeaderCell>
                <PhoneCell
                  positionFromRight="30%"
                  rowData={(rowData: any) => rowData}
                />
              </Column>
              <Column width={160} resizable>
                <CustomHeaderCell>{t("REGION")}</CustomHeaderCell>
                <CustomCell dataKey="region_name">
                  {(rowData: any) => (
                    <span>{displayField(rowData.region_name)}</span>
                  )}
                </CustomCell>
              </Column>

              <Column width={160} resizable>
                <CustomHeaderCell>{t("GROUP")}</CustomHeaderCell>
                <CustomCell dataKey="group_name">
                  {(rowData: any) => (
                    <span>{displayField(rowData.group_name)}</span>
                  )}
                </CustomCell>
              </Column>
            </Table>
          </div>
        </Content>
        <Footer className="footer">
          <Pagination
            ellipsis
            boundaryLinks
            maxButtons={5}
            size="xs"
            layout={["total"]}
            total={total}
          />
        </Footer>
        <ToastContainer />
      </Container>
    </div>
  );
}

export default CommercialScheduleStores;
